/**
 * The "Property development schedule" dialog shell (spec 5.1): a PASSIVE modal (dismiss
 * via the header ✕ — no footer Calculate/Close) with the header ("N scheduled slots
 * across M areas"), the Chart|Table toggle, the toolbar row (search / phase / future-only
 * / expand-all + bulk-shift + revert), the phase/marker legend, and the results-out-of-date
 * banner. It wires the read hook, the browser-local edit layer, and the selection, and it
 * has NO calculate of its own: the dialog only CAPTURES per-slot date overrides and flushes
 * them into the eval's working config (`scheduleSlotOverrides`), so the page's single main
 * Evaluate carries them alongside every other override (INS-10671; PRD AC 55).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import {
  ContentSwitcher,
  IconButton,
  InlineLoading,
  InlineNotification,
  Modal,
  Switch,
} from '@carbon/react';
import { CollapseAll, ZoomIn, ZoomOut, ZoomReset } from '@carbon/react/icons';
import { FormattedCount } from 'components/formatted-count';
import { formatNumber } from 'utils/number';
import { NavigationGuard } from 'components/navigation-guard';
import { useEval3Page } from '../context';
import { PHASE_COLORS, PHASE_LABELS, PHASE_ORDER } from './constants';
import {
  buildRowModel,
  domainRange,
  overlayRowDates,
  windowLabel as computeWindowLabel,
} from './utils';
import {
  replaceWith,
  selectionCount,
  selectionToPoints,
} from './selection-model';
import {
  expandedGroupsAtom,
  futureOnlyAtom,
  phaseFilterAtom,
  scheduleFocusSearchAtom,
  searchQueryAtom,
  selectionAtom,
  unscheduledExpandedAtom,
  viewModeAtom,
} from './atoms';
import {
  buildCommitOverrides,
  splitCommittedOverrides,
  type PreservedOverrideWire,
} from './edit-ops';
import { useScheduleEdits } from './use-schedule-edits';
import {
  useSchedulePayload,
  ScheduleNotReadyError,
  type ScheduleDraftConfigurations,
} from './use-schedule-payload';
import { ScheduleGantt } from './schedule-gantt';
import { ScheduleTable } from './schedule-table';
import { ScheduleToolbar } from './schedule-toolbar';
import { BulkShiftToolbar } from './bulk-shift-toolbar';
import { RevertMenu } from './revert-menu';
import { serverStalenessSentences, staleBannerSubtitle } from './staleness';
import type {
  BulkShiftResult,
  MilestoneKey,
  ScheduleOverrideEntry,
  SlotId,
  StalenessInfo,
} from './types';
import styles from './schedule-dialog.module.scss';

// Chart zoom (INS-10671): the visible window is expressed as a [start, end] FRACTION of the
// full domain, so it survives base-domain shifts (future-only toggle, edits) proportionally.
// Zoom-in shrinks the window to 60% of its current span (centered); zoom-out is the inverse.
// The window never shrinks below MIN_ZOOM_FRACTION of the full range so markers stay legible.
const ZOOM_IN_FACTOR = 0.6;
const MIN_ZOOM_FRACTION = 0.08;

export interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The committed INDIVIDUAL date/volume ops-log to seed the working copy from (the
   * `individual` subset of the page's WorkingConfig.slotOverrides — the ops the dialog edits).
   */
  committedOverrides?: ScheduleOverrideEntry[];
  /**
   * The eval's NON-individual override ops (bulk / filter / ids) the dialog does not edit
   * but must preserve verbatim on commit, so a Calculate never drops them.
   */
  preservedOverrides?: PreservedOverrideWire[];
  /** The draft `configurations` fragment for the pre-run POST (blob paths). */
  draftConfigurations?: ScheduleDraftConfigurations;
  /** Whether scheduling has completed — gates the read + the schedule-first empty state. */
  scheduleComplete?: boolean;
  /** Whether the calc has produced results (suppresses the out-of-date banner pre-first-run). */
  hasResults?: boolean;
  /**
   * Cross-session staleness for the viewed calc (spec 06-VER), threaded in from the eval
   * page's existing `useCalcDetail` read (`CalcDetail.staleness`) — NOT a new fetch. When
   * `stale`, its server reasons compose into the SAME out-of-date banner as any in-session
   * edits; the server returns `stale: false` for the steady state and never-calculated
   * evals, so no client suppression logic is needed.
   */
  staleness?: StalenessInfo;
  /** The eval being re-run in place (omitted forces a new-eval fork). */
  existingEvaluationId?: number;
  /**
   * INS-10671 — replaces the dialog's contribution to the eval's working config so the
   * SINGLE main Evaluate carries the schedule edits (the dialog no longer runs its own
   * calculate). Called whenever the working copy changes while open; a whole-array
   * REPLACE, so a reopen+edit never duplicates the prior contribution.
   */
  onScheduleOverridesChange: (ops: PreservedOverrideWire[]) => void;
  /**
   * The working config's CURRENT schedule contribution (wire ops), so a reopen restores
   * prior UNCOMMITTED edits into the display instead of dropping them. Empty when none.
   */
  pendingScheduleOverrides?: PreservedOverrideWire[];
  /**
   * Live per-slot date edits captured in the Properties/Slot-view grid (the working
   * config's `slotOverrides`), converted to schedule entries. Overlaid onto the dialog's
   * BASE rows for DISPLAY only (chart + Table) so a grid date edit (permit / spud / first
   * production) is reflected LIVE — before the page's single Evaluate. NOT merged into the
   * dialog's own edit log, so it can never double-count against `slotOverrides` on Calculate.
   * Empty when none.
   */
  slotDateOverrides?: ScheduleOverrideEntry[];
  /**
   * INS-11293 — the Development Pace Scenario name driving this schedule, resolved from the
   * evaluation case's `development_pace` area assignments. Shown as the dialog subtitle so it
   * reflects the DPS scenario the content follows, NOT the eval's own Scenario label. Null when
   * the case has zero — or more than one — DPS scenario (no single name to show), in which case
   * the subtitle falls back to the case label.
   */
  developmentPaceScenarioName?: string | null;
}

/**
 * Order- and object-key-insensitive equality for two calculate-wire override arrays.
 * The dialog's freshly-built contribution and the working config's current value can
 * carry the same ops in a different order (persisted vs. canonical) or with different
 * object-key order (BE-emitted vs. FE-built), so a structural set-compare — not a
 * reference or naive `JSON` compare — is what distinguishes a real edit from a no-op
 * re-open. A no-op must NOT write: writing would dirty the page form (TanStack
 * `setFieldValue` is touch-based) and could overwrite a carried-forward set.
 *
 * @param {PreservedOverrideWire[]} a - One override-op array.
 * @param {PreservedOverrideWire[]} b - The other.
 * @return {boolean} True when both hold the same ops regardless of order.
 */
function sameOverrideOps(
  a: PreservedOverrideWire[],
  b: PreservedOverrideWire[]
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const stable = (value: unknown): string =>
    JSON.stringify(value, (_key, val) => {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        return Object.fromEntries(
          Object.entries(val as Record<string, unknown>).sort(([x], [y]) =>
            x.localeCompare(y)
          )
        );
      }
      return val;
    });
  const left = a.map(stable).sort();
  const right = b.map(stable).sort();
  return left.every((entry, index) => entry === right[index]);
}

/**
 * Renders the schedule dialog.
 *
 * @param {ScheduleDialogProps} props - Open state, close handler, seed data, and gating flags.
 * @return {JSX.Element | null} The modal, or null when closed.
 */
export function ScheduleDialog({
  open,
  onClose,
  committedOverrides = [],
  preservedOverrides = [],
  draftConfigurations,
  scheduleComplete = true,
  hasResults = false,
  staleness,
  existingEvaluationId,
  onScheduleOverridesChange,
  pendingScheduleOverrides = [],
  slotDateOverrides = [],
  developmentPaceScenarioName = null,
}: ScheduleDialogProps) {
  const { packageId, calculationId } = useEval3Page();

  const [viewMode, setViewMode] = useAtom(viewModeAtom);
  const [selection, setSelection] = useAtom(selectionAtom);
  const [expandedGroups, setExpandedGroups] = useAtom(expandedGroupsAtom);
  const [unscheduledExpanded, setUnscheduledExpanded] = useAtom(
    unscheduledExpandedAtom
  );
  const [futureOnly, setFutureOnly] = useAtom(futureOnlyAtom);
  const [search, setSearch] = useAtom(searchQueryAtom);
  // A pending "focus this well" request from the Properties grid's date-cell affordance
  // (INS-12497). Consumed once on open to seed the search, then cleared.
  const [focusSearch, setFocusSearch] = useAtom(scheduleFocusSearchAtom);
  const [phaseFilter, setPhaseFilter] = useAtom(phaseFilterAtom);

  const edits = useScheduleEdits();

  const [bulkResult, setBulkResult] = useState<BulkShiftResult | null>(null);
  const [windowText, setWindowText] = useState('');
  // The visible time window as a [start, end] fraction of the full domain (null = full range).
  const [zoom, setZoom] = useState<{ start: number; end: number } | null>(null);
  // The count of date writes the most recent applied CSV upload merged — surfaced in the
  // out-of-date banner (AC 10). Reset on open and superseded by any later Calculate.
  const [appliedUploadCount, setAppliedUploadCount] = useState(0);
  // True only once THIS open's seed has applied to the working copy. The flush must wait
  // for it: seeding is a jotai setState that lands on a later render, so a flush in the
  // same pass would read the pre-seed (stale) working copy and write it over the eval's
  // carried-forward overrides — the intra-render form of the fork data-loss bug.
  const [isSeeded, setIsSeeded] = useState(false);
  // Defect 3 (INS-11293) — an explicit "Reset all dates" must survive a close/reopen. The
  // seed re-hydrates the working copy from the durable form field every open, so once the
  // user has cleared it, the seed must NOT resurrect the committed (last-calc) dates. The
  // dialog stays mounted across close (it renders null), so a ref persists between opens:
  //  - `clearedRef` remembers a reset within ONE eval/case context;
  //  - `seededPayloadRef` drops that intent when the context changes (eval switch / re-run),
  //    so a reset never leaks onto a different eval's committed dates.
  const clearedRef = useRef(false);
  const seededPayloadRef = useRef<unknown>(undefined);

  // The read path keys on whether a calc EXISTS, not on the route's draft flag: a
  // case-selected eval that has never been calculated (a from-scratch draft OR a browse
  // view with a picked case) reads pre-run through the POST; an existing calc reads its
  // frozen schedule via the GET.
  const isPreRun = calculationId === null;
  const params = useMemo(() => {
    if (isPreRun) {
      return {
        mode: 'draft' as const,
        packageId,
        configurations: draftConfigurations ?? {},
      };
    }
    return { mode: 'calc' as const, calculationId };
  }, [isPreRun, packageId, calculationId, draftConfigurations]);

  const enabled =
    open &&
    scheduleComplete &&
    (isPreRun ? draftConfigurations !== undefined : true);

  const {
    data: payload,
    isPending,
    error,
  } = useSchedulePayload(params, enabled);

  // Seed on open (when the payload arrives): the baseline is the committed (last-calc)
  // set — the dirty flag compares against it — while the working copy restores any prior
  // UNCOMMITTED edits already captured in the eval's working config, so closing and
  // reopening the dialog never drops them. No pending edits → working = committed.
  useEffect(() => {
    if (!(open && payload)) {
      // Not seeded while closed or before the payload arrives — the flush stays parked.
      setIsSeeded(false);
      return;
    }
    // A new eval/case context (payload identity changed) starts a fresh session: any prior
    // "Reset all dates" belonged to the previous context, so drop it before seeding.
    if (seededPayloadRef.current !== payload) {
      seededPayloadRef.current = payload;
      clearedRef.current = false;
    }
    const pending = splitCommittedOverrides(pendingScheduleOverrides).committed;
    // An EMPTY persisted field is ambiguous, so resolve it per path (Defect 3):
    //  - Fork (saved eval / draft): `scheduleSlotOverrides` is pre-seeded to the committed
    //    set, so an empty field can ONLY mean the user cleared it — honor empty, never
    //    resurrect the committed dates (and so the reopen's flush keeps the field empty
    //    instead of re-writing the committed ops back onto the next Calculate).
    //  - In-place re-run: the field holds only NEW ops (the reused blob carries the
    //    committed set), so a first open seeds the working copy FROM committed — unless the
    //    user reset this session, which `clearedRef` carries across the reopen.
    const isFork = existingEvaluationId === undefined;
    const working =
      pending.length > 0
        ? pending
        : isFork || clearedRef.current
          ? []
          : committedOverrides;
    edits.seed(committedOverrides, working);
    setIsSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload]);

  // INS-10671 — flush the dialog's edits into the eval's working config so the SINGLE
  // main Evaluate carries them (there is no dialog-owned calculate). In-place re-run:
  // only the individual date ops (the reused blob already holds prior committed
  // overrides), empty when clean. Fork (saved eval / draft): the full set — the preserved
  // bulk/filter ops PLUS the individual dates — so the new eval never drops them. A
  // whole-array REPLACE, so a reopen+edit never duplicates the prior contribution.
  useEffect(() => {
    // Wait for THIS open's seed to apply (isSeeded): flushing before it would read the
    // pre-seed working copy and, on a saved-eval fork, overwrite the carried-forward
    // overrides with a preserved-only stale value — silent data loss if the schedule read
    // is slow/errors or the dialog is closed mid-load (AC 56/57).
    if (!open || !payload || !isSeeded) {
      return;
    }
    const ops =
      existingEvaluationId !== undefined
        ? edits.hasUncommittedEdits
          ? buildCommitOverrides([], edits.workingEntries)
          : []
        : buildCommitOverrides(preservedOverrides, edits.workingEntries);
    // Only write on a real change: merely opening/closing the dialog must not dirty the
    // page form (TanStack `setFieldValue` is touch-based → would enable a spurious recalc
    // and a false unsaved-changes prompt), and a no-op re-open must not disturb the field.
    if (!sameOverrideOps(ops, pendingScheduleOverrides)) {
      onScheduleOverridesChange(ops);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    payload,
    isSeeded,
    existingEvaluationId,
    edits.hasUncommittedEdits,
    edits.workingEntries,
    preservedOverrides,
  ]);

  // Each open starts fresh: future-only ON, no selection/filters — the module-scoped
  // atoms otherwise carry a prior session's transient UI state. (Group expand state is
  // seeded to fully-expanded by the payload effect below, once the groups are known.)
  useEffect(() => {
    if (!open) {
      return;
    }
    setViewMode('chart');
    setSelection(new Set());
    setFutureOnly(true);
    // Seed the search from a grid "focus this well" request (INS-12497), else start blank.
    // One-shot: consume the focus so a later panel-button open is unfiltered.
    setSearch(focusSearch ?? '');
    setFocusSearch(null);
    setPhaseFilter(null);
    setBulkResult(null);
    setAppliedUploadCount(0);
    setZoom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Default to fully expanded: once the schedule payload arrives, expand every area group
  // (and the "Unscheduled" pseudo-group) so each open shows all scheduled properties
  // without drilling in. Keyed on the raw payload (stable once fetched), so a user's later
  // manual collapse of a group sticks — this only re-seeds on a reopen or refetch.
  useEffect(() => {
    if (!(open && payload)) {
      return;
    }
    setExpandedGroups(new Set(payload.groups.map((group) => group.id)));
    setUnscheduledExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload]);

  // INS-10671 — LIVE overlay of the Properties/Slot-view per-cell date edits (permit / spud /
  // first production, captured in the working config's `slotOverrides` and threaded in as
  // `slotDateOverrides`) onto the dialog's BASE rows, so a grid date edit reflects in the chart +
  // Table immediately — before the page's single Evaluate. DISPLAY ONLY: the overlay is baked into
  // the base rows the row model reads, never into the dialog's own edit log (`edits.workingEntries`),
  // so it can't double-count against `slotOverrides` on Calculate and the dialog's own drag / table
  // edits still overlay ON TOP. `overlayRowDates` skips reported/locked milestones, so a grid edit
  // never moves a locked date here either. Rows with no grid edit are kept by reference (stable memo
  // identity → the row-model / table memos below don't churn).
  const effectivePayload = useMemo(() => {
    if (!payload) {
      return null;
    }
    if (slotDateOverrides.length === 0) {
      return payload;
    }
    const overridden = new Set(slotDateOverrides.map((entry) => entry.slotId));
    const rows = payload.rows.map((row) => {
      if (!overridden.has(row.slotId)) {
        return row;
      }
      return { ...row, dates: overlayRowDates(row, slotDateOverrides) };
    });
    return { ...payload, rows };
  }, [payload, slotDateOverrides]);

  // -- row model + domain ---------------------------------------------------
  const rowModel = useMemo(() => {
    if (!effectivePayload) {
      return null;
    }
    return buildRowModel(
      effectivePayload.rows,
      effectivePayload.groups,
      edits.workingEntries,
      {
        expandedGroupIds: expandedGroups,
        unscheduledExpanded,
        futureOnly,
        effectiveDate: effectivePayload.effectiveDate,
        search,
        phaseFilter,
      }
    );
  }, [
    effectivePayload,
    edits.workingEntries,
    expandedGroups,
    unscheduledExpanded,
    futureOnly,
    search,
    phaseFilter,
  ]);

  const domain = useMemo(() => {
    if (!effectivePayload) {
      return { startMs: 0, endMs: 1 };
    }
    const rowDates = effectivePayload.rows.map((row) =>
      overlayRowDates(row, edits.workingEntries)
    );
    return domainRange(rowDates, futureOnly, effectivePayload.effectiveDate);
  }, [effectivePayload, edits.workingEntries, futureOnly]);

  // The window actually rendered: the full domain, or the zoom sub-window projected onto it.
  // The Gantt clamps every mark to [0, 100]% (via `toPercent`), so out-of-window markers pin
  // to the edges rather than overflow — no chart-internal change is needed for zoom.
  const effectiveDomain = useMemo(() => {
    if (!zoom) {
      return domain;
    }
    const span = domain.endMs - domain.startMs;
    return {
      startMs: domain.startMs + zoom.start * span,
      endMs: domain.startMs + zoom.end * span,
    };
  }, [domain, zoom]);

  const defaultWindowText = useMemo(
    () => computeWindowLabel(effectiveDomain.startMs, effectiveDomain.endMs),
    [effectiveDomain.startMs, effectiveDomain.endMs]
  );

  // -- handlers -------------------------------------------------------------
  const handleSelectionChange = useCallback(
    (points: Array<{ slotId: SlotId; field: MilestoneKey }>) => {
      setSelection(replaceWith(points));
    },
    [setSelection]
  );

  // Relative-month bulk shift over the selected points (spec 5.3): apply through the edit
  // layer and surface the applied/skipped summary in the toolbar. Shifts against the
  // grid-overlaid rows (effectivePayload) so its order checks match what's displayed —
  // the same base the multi-marker drag uses.
  const handleBulkShift = useCallback(
    (offsetMonths: number) => {
      if (!effectivePayload) {
        return;
      }
      const points = selectionToPoints(selection);
      const result = edits.bulkShift(
        effectivePayload.rows,
        points,
        offsetMonths
      );
      setBulkResult(result);
    },
    [effectivePayload, selection, edits]
  );

  // Per-unit chevron toggle (INS-10671): flip a unit's id in the expanded set, or the
  // "Unscheduled" pseudo-group (groupId null). The coverage drill-in still auto-expands
  // Unscheduled via setUnscheduledExpanded — this shares the same state.
  const handleToggleGroup = useCallback(
    (groupId: number | null) => {
      if (groupId === null) {
        setUnscheduledExpanded((expanded) => !expanded);
        return;
      }
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
    },
    [setExpandedGroups, setUnscheduledExpanded]
  );

  // Zoom the visible window in (factor < 1) or out (factor > 1) about its center, working in
  // domain fractions and clamping to [0, 1]. Zooming out to (or past) the full range clears
  // the window back to null so the chart returns to the natural domain.
  const applyZoom = useCallback((factor: number) => {
    setZoom((prev) => {
      const start = prev?.start ?? 0;
      const end = prev?.end ?? 1;
      const center = (start + end) / 2;
      const nextWidth = Math.min(
        1,
        Math.max(MIN_ZOOM_FRACTION, (end - start) * factor)
      );
      if (nextWidth >= 1) {
        return null;
      }
      let nextStart = center - nextWidth / 2;
      let nextEnd = center + nextWidth / 2;
      if (nextStart < 0) {
        nextStart = 0;
        nextEnd = nextWidth;
      } else if (nextEnd > 1) {
        nextEnd = 1;
        nextStart = 1 - nextWidth;
      }
      return { start: nextStart, end: nextEnd };
    });
  }, []);

  // Collapse every expanded unit group (and the "Unscheduled" pseudo-group).
  const handleCollapseAll = useCallback(() => {
    setExpandedGroups(new Set());
    setUnscheduledExpanded(false);
  }, [setExpandedGroups, setUnscheduledExpanded]);

  const zoomWidth = zoom ? zoom.end - zoom.start : 1;
  const canZoomIn = zoomWidth > MIN_ZOOM_FRACTION + 1e-6;
  const canZoomOut = zoom !== null;
  const canCollapseAll = expandedGroups.size > 0 || unscheduledExpanded;

  if (!open) {
    return null;
  }

  // One banner, two sources (spec 06-VER): the server's cross-session staleness reasons
  // (rendered only when `stale`) compose with the dialog's own in-session edit reason into
  // a single ordered cause list — never two stacked notices.
  const serverStaleClauses = serverStalenessSentences(staleness);
  let inSessionClause: string | null = null;
  if (hasResults && edits.hasUncommittedEdits) {
    inSessionClause =
      appliedUploadCount > 0
        ? `${formatNumber({
            value: appliedUploadCount,
            minPrecision: 0,
            includeCommas: true,
          })} uploaded date change(s) not yet calculated`
        : 'unsaved schedule edits';
  }
  const staleClauses = inSessionClause
    ? [...serverStaleClauses, inSessionClause]
    : serverStaleClauses;
  const showStaleBanner = staleClauses.length > 0;
  const notReady = error instanceof ScheduleNotReadyError;

  return (
    // Passive modal (INS-10671): the dialog captures edits into the eval's working config
    // and dismisses via the header ✕ / Escape — there is no dialog-owned Calculate. The
    // single main Evaluate applies the schedule edits alongside every other override; a
    // locked/out-of-order rejection surfaces there (the page's calculate error).
    <Modal
      open
      passiveModal
      // Dismiss only via the header ✕ / Escape — never an outside click. The revert-all
      // confirm ("Discard N edited dates?") is portaled to <body> (to center it), so it
      // lives OUTSIDE this modal's DOM; without this, clicking "Discard edits" reads as an
      // outside click and closes this modal mid-reset. That close skips the effect that
      // flushes the cleared edits into the working config, so a reopen re-seeds the stale
      // edit and the reset appears not to stick (INS-10671).
      preventCloseOnClickOutside
      size="lg"
      className={styles.modal}
      modalHeading="Property development schedule"
      onRequestClose={onClose}
    >
      <NavigationGuard
        hasChanges={edits.hasUncommittedEdits}
        message="You have unsaved schedule edits that will be discarded if you leave."
      />

      {effectivePayload && (
        <p className={styles.subhead}>
          {/* Lead with the DPS scenario driving the schedule (Defect 6); fall back to the
              case label when no single DPS scenario names the whole case. */}
          {developmentPaceScenarioName ?? effectivePayload.caseName} ·{' '}
          <FormattedCount value={effectivePayload.scheduledSlotCount} />{' '}
          scheduled slots across{' '}
          <FormattedCount value={effectivePayload.groups.length} /> areas
        </p>
      )}

      {showStaleBanner && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Results are out of date"
          subtitle={staleBannerSubtitle(staleClauses)}
          className={styles.banner}
        />
      )}

      {(effectivePayload?.runWarnings?.length ?? 0) > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="How this run was valued"
          subtitle={effectivePayload?.runWarnings?.join(' ')}
          className={styles.banner}
        />
      )}

      {isPending && (
        <InlineLoading
          description="Loading schedule…"
          className={styles.loading}
        />
      )}

      {notReady && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="No schedule yet"
          subtitle="Schedule the scenario first, then open the schedule."
          className={styles.banner}
        />
      )}

      {effectivePayload && rowModel && (
        <>
          <div className={styles.controlRow}>
            <ContentSwitcher
              size="sm"
              className={styles.viewSwitch}
              selectedIndex={viewMode === 'chart' ? 0 : 1}
              onChange={({ index }) =>
                setViewMode(index === 0 ? 'chart' : 'table')
              }
            >
              <Switch name="chart" text="Chart" />
              <Switch name="table" text="Table" />
            </ContentSwitcher>
            <div className={styles.controlRowRight}>
              {/* ⋮ revert menu FIRST (left of the zoom controls): keeps it away from
                  the tear sheet's right edge + the ✕ close button, so its dropdown
                  opens inward with room instead of clipping in the corner (INS-10671). */}
              <RevertMenu
                selection={selection}
                editedDateCount={edits.editedDateCount}
                onRevert={(scope) => {
                  edits.revertScope(scope);
                  if (scope.kind === 'all') {
                    // Remember the explicit clear so a close/reopen honors it (Defect 3):
                    // otherwise the seed re-hydrates the committed dates from the fallback.
                    clearedRef.current = true;
                  }
                  if (scope.kind === 'all' || scope.kind === 'selection') {
                    setSelection(new Set());
                  }
                }}
              />
              {viewMode === 'chart' && (
                <div
                  className={styles.chartControls}
                  role="group"
                  aria-label="Chart controls"
                >
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="bottom"
                    label="Zoom in"
                    disabled={!canZoomIn}
                    onClick={() => applyZoom(ZOOM_IN_FACTOR)}
                  >
                    <ZoomIn />
                  </IconButton>
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="bottom"
                    label="Zoom out"
                    disabled={!canZoomOut}
                    onClick={() => applyZoom(1 / ZOOM_IN_FACTOR)}
                  >
                    <ZoomOut />
                  </IconButton>
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="bottom"
                    label="Reset zoom"
                    disabled={!canZoomOut}
                    onClick={() => setZoom(null)}
                  >
                    <ZoomReset />
                  </IconButton>
                  <IconButton
                    kind="ghost"
                    size="sm"
                    align="bottom"
                    label="Collapse all"
                    disabled={!canCollapseAll}
                    onClick={handleCollapseAll}
                  >
                    <CollapseAll />
                  </IconButton>
                </div>
              )}
            </div>
          </div>

          <ScheduleToolbar
            windowLabel={windowText || defaultWindowText}
            shownRowCount={rowModel.shownRowCount}
            totalRowCount={rowModel.totalRowCount}
          />

          <BulkShiftToolbar
            selectedCount={selectionCount(selection)}
            onApply={handleBulkShift}
            onClear={() => setSelection(new Set())}
            lastResult={bulkResult}
          />

          <ScheduleLegend />

          {viewMode === 'chart' ? (
            <ScheduleGantt
              model={rowModel}
              domain={effectiveDomain}
              selection={selection}
              order={effectivePayload.constraints.order}
              snapToMonth={effectivePayload.constraints.monthStartModeled}
              onDragCommit={edits.applyDrag}
              onMultiDragCommit={(primary, others, deltaMs) =>
                edits.applyMultiDrag(
                  effectivePayload.rows,
                  primary,
                  others,
                  deltaMs
                )
              }
              onSelectionChange={handleSelectionChange}
              onToggleGroup={handleToggleGroup}
              horizontalScroll
              onWindowChange={(startMs, endMs) =>
                setWindowText(computeWindowLabel(startMs, endMs))
              }
            />
          ) : (
            <div
              className={styles.tableMount}
              aria-label="Schedule table view"
              data-testid="schedule-table-mount"
            >
              <ScheduleTable
                payload={effectivePayload}
                onUploadApplied={setAppliedUploadCount}
              />
            </div>
          )}

          {effectivePayload.rows.length === 0 && (
            <p className={styles.empty}>No schedulable slots for this case.</p>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * The phase-segment + marker-state legend (AC 16).
 *
 * @return {JSX.Element} The legend row.
 */
function ScheduleLegend() {
  return (
    <div className={styles.legend} aria-label="Legend">
      {PHASE_ORDER.map((phase) => (
        <span key={phase} className={styles.legendItem}>
          <span
            className={styles.swatch}
            style={{ backgroundColor: PHASE_COLORS[phase] }}
          />
          {PHASE_LABELS[phase]}
        </span>
      ))}
    </div>
  );
}
